import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, Button, IconButton, Popover, Typography, useTheme } from '@mui/material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import moment from 'moment'
import { useAtomValue, useSetAtom } from 'jotai'
import * as sx from '../../common/stylesInline/report_daily_branch'
import { Item } from '../../common/snippets'
import Datepicker from '../../common/datePicker'
import { ReportHeading } from './common/ReportHeading'
import { GetRequest } from '../../common/api'
import { urls, xlCol, xlColq } from '../../common/constants'
import useToken from '../../helpers/useTokens'
import { companySettingsAtom } from '../../common/jotaiState/commonStateAtoms'
import { alertAtom, globalLoaderAtom } from '../../common/jotaiState/globalStateAtoms'
import DropDownOA from '../../common/dropDownOA'
import { TableComponent } from '../../common/TableComponent'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from '../../../i18n/I18nProvider'

const emptyGeneralSettings = {}

const messageTypeOptions = [
  { id: 'UTILITY', name: 'Utility', translationKey: 'waUtility' },
  { id: 'AUTHENTICATION', name: 'Authentication', translationKey: 'waAuthentication' },
  { id: 'MARKETING', name: 'Marketing', translationKey: 'waMarketing' },
  { id: 'SERVICE', name: 'Service', translationKey: 'waService' },
  { id: 'BUSINESS', name: 'Business Initiated', translationKey: 'waBusinessInitiated' },
  { id: 'CUSTOMER', name: 'Customer Initiated', translationKey: 'waCustomerInitiated' },
]
const policyCategoryOptions = [
  { id: 'Cargo', name: 'Cargo', translationKey: 'waCargo' },
]
const sentStatusOptions = [
  { id: 1, name: 'Success', translationKey: 'waSuccess' },
  { id: 0, name: 'Failed', translationKey: 'waFailed' },
]
const billingStatusOptions = [
  { id: 1, name: 'Billed', translationKey: 'waBilled' },
  { id: 0, name: 'Not Billed', translationKey: 'waNotBilled' },
]

const getPreviewPosition = (buttonRect) => {
  const cardWidth = 380
  const gutter = 12
  const viewportPadding = 16
  const spaceOnRight = window.innerWidth - buttonRect.right
  const left =
    spaceOnRight >= cardWidth + gutter ? buttonRect.right + gutter : Math.max(viewportPadding, buttonRect.left - cardWidth - gutter)
  const top = Math.min(Math.max(viewportPadding, buttonRect.top - 24), window.innerHeight - 220)

  return { top, left }
}

export const WhatsAppMessageSentDetails = () => {
  const { translations, language } = useTranslation()
  const gs = translations?.generalSettings || emptyGeneralSettings
  const tr = useCallback((key, fallback) => gs?.[key] || fallback, [gs])
  const translateOptions = useCallback(
    (options) => options.map((option) => ({ ...option, name: tr(option.translationKey, option.name) })),
    [tr]
  )
  const translatedMessageTypeOptions = useMemo(() => translateOptions(messageTypeOptions), [translateOptions])
  const translatedPolicyCategoryOptions = useMemo(() => translateOptions(policyCategoryOptions), [translateOptions])
  const translatedSentStatusOptions = useMemo(() => translateOptions(sentStatusOptions), [translateOptions])
  const translatedBillingStatusOptions = useMemo(() => translateOptions(billingStatusOptions), [translateOptions])
  const reportName = tr('waCommSentDetailReport', 'Whatsapp Communication Sent Detail Report')
  const { loginData } = useToken()
  const [, setSearchParams] = useSearchParams()
  const { intCompanyID } = loginData || {}
  const companySettings = useAtomValue(companySettingsAtom)
  const { WhatsappTypeID } = companySettings || {}
  const setAlert = useSetAtom(alertAtom)
  const setLoader = useSetAtom(globalLoaderAtom)
  const requestFailedMessage = useRef(tr('requestFailed', 'Request Failed'))
  requestFailedMessage.current = tr('requestFailed', 'Request Failed')
  const theme = useTheme()
  const [fromDate, setFromDate] = useState(moment(new Date()))
  const [toDate, setToDate] = useState(moment(new Date()))
  const [messageType, setMessageType] = useState(messageTypeOptions[0])
  const [policyType, setPolicyType] = useState([])
  const [policyCategory, setPolicyCategory] = useState(policyCategoryOptions[0])
  const [sentStatus, setSentStatus] = useState(sentStatusOptions[0])
  const [billingStatus, setBillingStatus] = useState(billingStatusOptions[0])
  const [policyTypeList, setPolicyTypeList] = useState([])
  const [rawTableData, setRawTableData] = useState([])
  const [previewPosition, setPreviewPosition] = useState(null)
  const [previewData, setPreviewData] = useState({
    Header: '',
    MessageTemplate: '',
  })
  const criteriaHeaders = useMemo(
    () => [
      { header: tr('waSentFromDate', 'Sent From Date'), field: 'FromDate', xlCol: { dataType: 'string' } },
      { header: tr('waSentToDate', 'Sent To Date'), field: 'ToDate', xlCol: { dataType: 'string' } },
      { header: tr('waMessageType', 'Message Type'), field: 'MessageType', xlCol: { dataType: 'string' } },
      { header: tr('waPolicyType', 'Policy Type'), field: 'PolicyType', xlCol: { dataType: 'string' } },
      { header: tr('waPolicyCategory', 'Policy Category'), field: 'PolicyCategory', xlCol: { dataType: 'string' } },
      { header: tr('waSentStatus', 'Sent Status'), field: 'SentStatus', xlCol: { dataType: 'string' } },
      { header: tr('waBillingStatus', 'Billing Status'), field: 'BillingStatus', xlCol: { dataType: 'string' } },
    ],
    [tr]
  )
  const WhatsAppDetailedHeaders = useMemo(() => {
    const header = (key, fallback, field, column, weight, extra = {}) => {
      const label = tr(key, fallback)
      return { header: label, exportHead: label, field, xlCol: column, weight, ...extra }
    }

    return [
      header('sn', 'SN', 'SN', xlCol, 8, { align: 'right' }),
      header('waCustomerMobileNo', 'Customer Mobile No', 'MobileNO', xlCol, 18),
      header('waMessageSentOn', 'Message Sent On', 'SentDateTime', xlCol, 24),
      header('waSessionStartedOn', 'Session Started On', 'SessionStartTime', xlCol, 18),
      header('waMessageCategory', 'Whatsapp Message Category', 'Category', xlCol, 18),
      header('waPolicyTypeMessageDetail', 'Policy Type Message Detail', 'Header', xlCol, 34),
      header('waViewMessage', 'View Message', 'ViewMsg_view', xlCol, 14, { ViewOnly: true }),
      header('waPolicyCategory', 'Policy Category', 'Master', xlCol, 18),
      header('waSentStatus', 'Sent Status', 'ISSent', xlColq, 16),
      header('waBillingStatus', 'Billing Status', 'IsBilled', xlColq, 16),
      header('waRateWithGst', 'Rate w/GST', 'RateAppliedWithGST', xlColq, 14, { align: 'right' }),
      header('waRemarksIfAny', 'Remarks, if any', 'Remarks', xlCol, 25),
    ]
  }, [tr])

  const criteriaData = useMemo(
    () => ({
      FromDate: moment(fromDate).format('DD-MMM-YYYY'),
      ToDate: moment(toDate).format('DD-MMM-YYYY'),
      MessageType: messageType?.name || '',
      PolicyType: policyType?.name || '',
      PolicyCategory: policyCategory?.name || '',
      SentStatus: sentStatus?.name || '',
      BillingStatus: billingStatus?.name || '',
      dataType: 'entry',
    }),
    [billingStatus, fromDate, messageType, policyCategory, policyType, sentStatus, toDate]
  )
  useEffect(() => {
    setMessageType((previous) => translatedMessageTypeOptions.find((option) => option.id === previous?.id) || translatedMessageTypeOptions[0])
    setPolicyCategory(
      (previous) => translatedPolicyCategoryOptions.find((option) => option.id === previous?.id) || translatedPolicyCategoryOptions[0]
    )
    setSentStatus((previous) => translatedSentStatusOptions.find((option) => option.id === previous?.id) || translatedSentStatusOptions[0])
    setBillingStatus(
      (previous) => translatedBillingStatusOptions.find((option) => option.id === previous?.id) || translatedBillingStatusOptions[0]
    )
  }, [
    translatedBillingStatusOptions,
    translatedMessageTypeOptions,
    translatedPolicyCategoryOptions,
    translatedSentStatusOptions,
  ])
  const fetchData = useCallback(() => {
    GetRequest(urls.whatsappPoliciesAndTemplates, { CompanyID: intCompanyID, WhatsappTypeID })
      .then((res) => {
        if (res.status !== true) {
          throw new Error(res.ErrMessage || requestFailedMessage.current)
        }
        if (!Array.isArray(res.Data.Table1) || res.Data.Table1.length === 0) {
          throw new Error(requestFailedMessage.current)
        }
        const list = [...res.Data.Table1.map((item) => ({ id: item.SMSType, name: item.Header }))]
        setPolicyTypeList(list)
        setPolicyType(list[0])
      })
      .catch((err) => {
        setAlert({ show: true, msg: err.message || requestFailedMessage.current, nextId: '' })
      })
      .finally(() => {
        setLoader(false)
      })
  }, [WhatsappTypeID, intCompanyID, setAlert, setLoader])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handlePreviewOpen = useCallback((event, row) => {
    const buttonRect = event.currentTarget.getBoundingClientRect()
    setPreviewPosition(getPreviewPosition(buttonRect))
    setPreviewData({
      Header: row?.Header || '',
      MessageTemplate: row?.MessageTemplate || tr('waNoPreviewHistory', 'No Preview History'),
    })
  }, [tr])

  const handlePreviewClose = () => {
    setPreviewPosition(null)
  }

  const tableData = useMemo(
    () =>
      rawTableData.map((item, index) => ({
        ...item,
        SN: index + 1,
        dataType: 'entry',
        ISSent: !item.ISSent ? tr('waFailed', 'Failed') : tr('waSuccess', 'Success'),
        IsBilled: !item.IsBilled ? tr('waNotBilled', 'Not Billed') : tr('waBilled', 'Billed'),
        ViewMsg_view: (
          <Button
            onClick={(event) => handlePreviewOpen(event, item)}
            sx={{ minWidth: 'auto', textTransform: 'capitalize', padding: '2px 8px' }}
          >
            {tr('waView', 'View')}
          </Button>
        ),
      })),
    [handlePreviewOpen, rawTableData, tr]
  )
  const handleGenerateReport = () => {
    const dateDifference = Math.abs(toDate.diff(fromDate, 'days'))

    if (dateDifference > 31) {
      setAlert({ show: true, msg: tr('waReportMax31Days', 'This report cannot be taken for more than 31 days'), nextId: '' })
      return
    }

    setLoader(true)
    setRawTableData([])
    const requestPayload = {
      CompanyID: intCompanyID,
      FromDate: moment(fromDate).format('YYYY-MM-DD'),
      ToDate: moment(toDate).format('YYYY-MM-DD'),
      MessageType: messageType.id,
      PolicyType: policyType.id,
      PolicyCategory: policyCategory.id,
      isSent: sentStatus.id,
      isBilled: billingStatus.id || 0,
      WhatsappType: WhatsappTypeID || 0,
    }

    GetRequest(urls.whatsappDetailedTransactionsReport, requestPayload)
      .then((response) => {
        if (response.status !== true) {
          throw new Error(response?.ErrMessage || tr('requestFailed', 'Request Failed'))
        }
        if (!Array.isArray(response.Data.Table) || response.Data.Table.length === 0) {
          throw new Error(tr('noDataFound', 'No Data Found'))
        }
        const list = response.Data.Table.map((item) => ({
          ...item,
          MessageTemplate: replacePlaceholdersWithArrayValues(item.MessageTemplate, item.MessageTags.split('|~|')),
        }))

        setRawTableData(list)
      })
      .catch((error) => {
        setAlert({ show: true, msg: error.message || tr('requestFailed', 'Request Failed'), nextId: '' })
        return null
      })
      .finally(() => {
        setLoader(false)
      })
  }

  function replacePlaceholdersWithArrayValues(originalString, replacementArray) {
    let count = 0
    const placeholderRegex = /\[[^\]]+\]/g
    const newString = originalString.replace(placeholderRegex, (match, index) => {
      const replString = replacementArray[count]
      count += 1
      return replString
    })
    return newString
  }
  const params = {
    view: 'General Settings',
    tab: 521,
  }
  const goToTabs = () => {
    setSearchParams(params)
  }
  return (
    <Item sx={{ padding: '0' }} className="reports">
      <ReportHeading reportName={reportName} />
      <Box sx={{ ...sx.reportFlex, justifyContent: 'start', paddingTop: '16px !important', gap: '12px' }}>
        <Item sx={{ ...sx.itemsx, minWidth: '230px' }} className="input--container bookingDatePicker">
          <Datepicker
            label={tr('waSentFromDate', 'Sent From Date')}
            name="whatsappMessageConsumptionFromDate"
            id="whatsappMessageConsumptionFromDate"
            value={fromDate}
            endDate={toDate}
            dateChange={setFromDate}
            width="100%"
          />
        </Item>
        <Item sx={{ ...sx.itemsx, minWidth: '230px' }} className="input--container bookingDatePicker">
          <Datepicker
            label={tr('waSentToDate', 'Sent To Date')}
            name="whatsappMessageConsumptionToDate"
            id="whatsappMessageConsumptionToDate"
            value={toDate}
            startDate={fromDate}
            dateChange={setToDate}
            width="100%"
          />
        </Item>
        <Item sx={{ ...sx.itemsx, minWidth: '230px' }}>
          <DropDownOA
            key={`whatsapp-message-type-${language}-${messageType?.id}`}
            id="whatsappCommunicationMessageType"
            label={tr('waMessageType', 'Message Type')}
            width="100%"
            data={translatedMessageTypeOptions}
            idKey="id"
            dataKey="name"
            value={messageType?.name || ''}
            defaultvalue={messageType?.name || ''}
            noCross={true}
            onSelect={(value) => setMessageType(value || translatedMessageTypeOptions[0])}
          />
        </Item>
        <Item sx={{ ...sx.itemsx, minWidth: '300px' }}>
          <DropDownOA
            id="whatsappCommunicationPolicyType"
            label={tr('waPolicyType', 'Policy Type')}
            width="100%"
            data={policyTypeList}
            idKey="id"
            dataKey="name"
            value={policyType?.name || ''}
            defaultvalue={policyType?.name || ''}
            noCross={true}
            onSelect={(value) => setPolicyType(value)}
          />
        </Item>
        <Item sx={{ ...sx.itemsx, minWidth: '230px' }}>
          <DropDownOA
            key={`whatsapp-policy-category-${language}-${policyCategory?.id}`}
            id="whatsappCommunicationPolicyCategory"
            label={tr('waPolicyCategory', 'Policy Category')}
            width="100%"
            data={translatedPolicyCategoryOptions}
            idKey="id"
            dataKey="name"
            value={policyCategory?.name || ''}
            defaultvalue={policyCategory?.name || ''}
            noCross={true}
            onSelect={(value) => setPolicyCategory(value || translatedPolicyCategoryOptions[0])}
          />
        </Item>
        <Item sx={{ ...sx.itemsx, minWidth: '230px' }}>
          <DropDownOA
            key={`whatsapp-sent-status-${language}-${sentStatus?.id}`}
            id="whatsappCommunicationSentStatus"
            label={tr('waSentStatus', 'Sent Status')}
            width="100%"
            data={translatedSentStatusOptions}
            idKey="id"
            dataKey="name"
            value={sentStatus?.name || ''}
            defaultvalue={sentStatus?.name || ''}
            noCross={true}
            onSelect={(value) => setSentStatus(value || translatedSentStatusOptions[0])}
          />
        </Item>
        <Item sx={{ ...sx.itemsx, minWidth: '230px' }}>
          <DropDownOA
            key={`whatsapp-billing-status-${language}-${billingStatus?.id}`}
            id="whatsappCommunicationBillingStatus"
            label={tr('waBillingStatus', 'Billing Status')}
            width="100%"
            data={translatedBillingStatusOptions}
            idKey="id"
            dataKey="name"
            value={billingStatus?.name || ''}
            defaultvalue={billingStatus?.name || ''}
            noCross={true}
            onSelect={(value) => setBillingStatus(value || translatedBillingStatusOptions[0])}
          />
        </Item>
        <Item sx={sx.itemsx}>
          <Button
            variant="contained"
            id="whatsappCommunicationLoadButton"
            style={{ ...sx.fullHeight, ...sx.novp }}
            className="btn btn-primary"
            onClick={handleGenerateReport}
          >
            {tr('gsLoad', 'LOAD')}
          </Button>
        </Item>
        <Item sx={sx.itemsx}>
          <Button style={{ ...sx.fullHeight, ...sx.novp }} className="btn btn-primary" variant="outlined" onClick={() => goToTabs()}>
            {tr('waBackToPolicy', 'Back to Policy')}
          </Button>
        </Item>
      </Box>
      {tableData.length ? (
        <TableComponent
          reportName={reportName}
          criteriaHeaders={criteriaHeaders}
          criteriaData={[criteriaData]}
          tableData={tableData}
          mainHeader={WhatsAppDetailedHeaders}
          isShowManageColumn={false}
        />
      ) : null}
      <Popover
        open={Boolean(previewPosition)}
        onClose={handlePreviewClose}
        anchorReference="anchorPosition"
        anchorPosition={previewPosition || { top: 0, left: 0 }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            width: 380,
            maxWidth: 'calc(100vw - 24px)',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 14px 32px rgba(0,0,0,0.22)',
            border: '1px solid rgba(0,0,0,0.06)',
          },
        }}
      >
        <Box
          sx={{
            backgroundColor: theme.palette.primary.main,
            color: '#fff',
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Typography sx={{ fontSize: '18px', fontWeight: 700, lineHeight: 1.2 }}>{previewData.Header}</Typography>
          <IconButton
            size="small"
            onClick={handlePreviewClose}
            sx={{
              color: '#fff',
              padding: '2px',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.12)' },
            }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </Box>
        <Box
          sx={{
            px: 2,
            py: 2,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontSize: '15px',
            fontWeight: 500,
            lineHeight: 1.55,
            color: '#333741',
            minHeight: 140,
            maxHeight: 320,
            overflowY: 'auto',
            backgroundColor: '#fff',
          }}
        >
          {previewData.MessageTemplate || tr('waNoPreviewHistory', 'No Preview History')}
        </Box>
      </Popover>
    </Item>
  )
}
